// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyBulkField = {
    /** The field name the model's bulk endpoint accepts. */
    key: string;
    label: string;
    kind: "select" | "date" | "number" | "toggle";
    options?: { value: string; label: string }[];
};

type Props = {
    count: number;
    fields?: TyBulkField[];
    /** Resolves `true` when the write landed, which is what clears the selection. */
    onApply: (set: Record<string, unknown>) => Promise<boolean>;
    onDelete?: () => void;
    onClear: () => void;
    busy?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The floating bar that rises when rows are selected. It is the only way to write the same value across
 * many rows — inline cells are disabled while a row is selected, so there is never a question of which
 * of the two last touched a field.
 *
 * Nothing here applies on change: a bulk write is the single most destructive thing on these screens
 * short of delete, and picking a value from a dropdown should never be the same gesture as committing
 * it to eighty documents.
 */
export default function BulkBar({ count, fields, onApply, onDelete, onClear, busy }: Props) {
    const t = useTranslation();
    const [draft, setDraft] = useState<Record<string, unknown>>({});

    if (!count) return null;

    const hasChanges = Object.keys(draft).length > 0;

    const handleApply = async () => {
        if (!hasChanges) return;
        const ok = await onApply(draft);
        if (ok) setDraft({});
    };

    return (
        <div className="bulk-bar" role="region" aria-label={t.table.bulkTitle}>
            <div className="bulk-bar-count">
                <span className="bulk-bar-count-value">{count}</span>
                <span className="bulk-bar-count-label">{t.table.bulkSelected}</span>
            </div>

            <div className="bulk-bar-fields">
                {(fields ?? []).map((field) => (
                    <label key={field.key} className="bulk-bar-field">
                        <span className="bulk-bar-field-label">{field.label}</span>

                        {field.kind === "select" && (
                            <select
                                className="bulk-bar-input"
                                value={(draft[field.key] as string) ?? ""}
                                onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            >
                                <option value="">—</option>
                                {(field.options ?? []).map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        )}

                        {(field.kind === "date" || field.kind === "number") && (
                            <input
                                className="bulk-bar-input"
                                type={field.kind}
                                value={(draft[field.key] as string) ?? ""}
                                onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            />
                        )}

                        {field.kind === "toggle" && (
                            <select
                                className="bulk-bar-input"
                                value={draft[field.key] === undefined ? "" : String(draft[field.key])}
                                onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value === "true" }))}
                            >
                                <option value="">—</option>
                                <option value="true">{t.table.bulkOn}</option>
                                <option value="false">{t.table.bulkOff}</option>
                            </select>
                        )}
                    </label>
                ))}
            </div>

            <div className="bulk-bar-actions">
                {fields && fields.length > 0 && (
                    <button type="button" className="bulk-bar-apply" onClick={handleApply} disabled={!hasChanges || busy}>
                        {t.table.bulkApply}
                    </button>
                )}

                {onDelete && (
                    <button type="button" className="bulk-bar-delete" onClick={onDelete} disabled={busy}>
                        <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                        <span>{t.table.bulkDelete}</span>
                    </button>
                )}

                <button type="button" className="bulk-bar-clear" onClick={onClear} disabled={busy} aria-label={t.table.bulkClear}>
                    <img src="/icons/close-icon.svg" alt="" width={12} height={12} />
                </button>
            </div>
        </div>
    );
}
